import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from './footer';

import '../styles/membershipForm.css';
import { applyNewMembership } from '../repository/applyMembershipRepository';
import { FaCheckCircle, FaEdit, FaUniversity, FaCopy } from 'react-icons/fa';
import MembershipHeader from './membership_header';
import qrScannerImg from '../assets/qr-scanner2.png';

const ApplyNewMembershipForm = () => {
  const navigate = useNavigate();
  
  const todayDate = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    fullName: '', 
    dob: todayDate, 
    gender: '',
    designation: '',
    institution: '',
    officeAddress: '',
    residentialAddress: '',
    email: '',
    mobile: '',
    qualifications: '',
    medicalCouncilNo: '',
    registrationCertificate: null, 
    areaOfInterest: '', 
    
    membershipCategory: 'Life Member', 
    paidAmount: '2500',

    paymentMode: '',
    utrNumber: '',
    paymentDate: todayDate
  });

  const [declarations, setDeclarations] = useState({
    applyMembership: false,
    agreeRules: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const interestsList = [
    "Genetics",
    "Imaging",
    "Research in Novel Therapeutics",
    "Pregnancy and Women’s health in PKD",
    "Managing Extra Renal Features of PKD",
    "Cerebral Aneurysms"
  ];

  const handleChipToggle = (interest) => {
    setFormData(prev => {
      const currentInterests = prev.areaOfInterest ? prev.areaOfInterest.split(', ') : [];
      let newInterests;

      if (currentInterests.includes(interest)) {
          newInterests = currentInterests.filter(i => i !== interest);
      } else {
          newInterests = [...currentInterests, interest];
      }

      return { 
        ...prev, 
        areaOfInterest: newInterests.join(', ') 
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      if (name === 'fullName') {
        newData.fullName = value.toUpperCase();
      }

      if (name === 'paymentMode' && value === 'Cash') {
        newData.utrNumber = '';
      }

      return newData;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      if (file.size > 1048576) {
        alert("File size must be less than 1MB. Please select a smaller file.");
        e.target.value = null; 
        setFormData(prev => ({ ...prev, registrationCertificate: null }));
      } else {
        setFormData(prev => ({ ...prev, registrationCertificate: file }));
      }
    } else {
        setFormData(prev => ({ ...prev, registrationCertificate: null }));
    }
  };

  const handleDeclarationChange = (e) => {
    setDeclarations({ ...declarations, [e.target.name]: e.target.checked });
  };

  const isFormValid = () => {
    if (!declarations.applyMembership || !declarations.agreeRules) {
        return false;
    }

    if (!formData.registrationCertificate) {
        return false;
    }

    const requiredFields = [
        'fullName', 
        'dob', 
        'gender', 
        'email', 
        'mobile', 
        'residentialAddress',
        'areaOfInterest',
        'membershipCategory', 
        'paidAmount', 
        'paymentMode', 
        'paymentDate'
    ];

    if (formData.paymentMode !== 'Cash') {
        requiredFields.push('utrNumber');
    }

    for (const field of requiredFields) {
        if (!formData[field] || formData[field].toString().trim() === '') {
            return false;
        }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
        alert("Please fill all required fields, attach the registration certificate, and accept the declarations.");
        return;
    }

    setIsSubmitting(true);

    const dataToSubmit = { ...formData };
    
    if (dataToSubmit.paymentMode === 'Cash') {
        dataToSubmit.utrNumber = null;
    }

    const result = await applyNewMembership(dataToSubmit, formData.registrationCertificate);

    setIsSubmitting(false);

    if (result.success) {
        setShowSuccessModal(true);
    } else {
        alert("❌ Error: " + result.message);
    }
  };

  const closeModal = () => {
    setShowSuccessModal(false);
    navigate('/');
  };

  const handleCopy = (text) => {
      navigator.clipboard.writeText(text);
  };

  return (
    <>
      <div className="membership-page-container">
        
        <div className="membership-form-card">
          <MembershipHeader/>

          <h2 className="membership-title">NEW MEMBERSHIP APPLICATION FORM</h2>

          <form onSubmit={handleSubmit}>
            
            <h4 className="proposer-title">1. Full Name (in block letters)</h4>
            <div className="membership-form-group">
              <input 
                type="text" 
                name="fullName" 
                value={formData.fullName} 
                onChange={handleChange} 
                className="membership-input input-uppercase" 
                required 
              />
            </div>

            <h4 className="proposer-title">2. Email ID & Mobile Number</h4>
            <div className="membership-row">
              <div className="membership-col">
                <label className="membership-label">Email ID:</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  className="membership-input" 
                  required 
                />
              </div>
              <div className="membership-col">
                <label className="membership-label">Mobile No.:</label>
                <input 
                  type="tel" 
                  name="mobile" 
                  value={formData.mobile} 
                  onChange={handleChange} 
                  className="membership-input" 
                  required 
                />
              </div>
            </div>

            <h4 className="proposer-title">3. Date of Birth & Gender</h4>
            <div className="membership-row">
              <div className="membership-col">
                <label className="membership-label">Date of Birth:</label>
                <input 
                  type="date" 
                  name="dob" 
                  value={formData.dob} 
                  onChange={handleChange} 
                  className="membership-input" 
                  max={todayDate}
                  required 
                />
              </div>
              <div className="membership-col">
                <label className="membership-label">Gender:</label>
                <select 
                  name="gender" 
                  value={formData.gender} 
                  onChange={handleChange} 
                  className="membership-select"
                  required
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <h4 className="proposer-title">4. Professional Qualification(s)</h4>
            <div className="membership-form-group">
              <input 
                type="text" 
                name="qualifications" 
                value={formData.qualifications} 
                onChange={handleChange} 
                className="membership-input" 
              />
            </div>

            <h4 className="proposer-title">5. Medical Council Registration Number (if applicable else submit None)</h4>
            <div className="membership-form-group">
              <input 
                type="text" 
                name="medicalCouncilNo" 
                value={formData.medicalCouncilNo} 
                onChange={handleChange} 
                className="membership-input" 
              />
            </div>

            <h4 className="proposer-title">Registration Certificate (Required)</h4>
            <div className="membership-form-group">
              <label className="membership-label" style={{ fontWeight: 'normal', fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                Please upload your certificate (Max file size: 1MB. Accepted formats: PDF only.)
              </label>
              <input 
                type="file" 
                name="registrationCertificate" 
                onChange={handleFileChange} 
                className="membership-input" 
                accept=".pdf"
                required
              />
            </div>

            <h4 className="proposer-title">6. Name of Institution / Organization</h4>
            <div className="membership-form-group">
              <input 
                type="text" 
                name="institution" 
                value={formData.institution} 
                onChange={handleChange} 
                className="membership-input" 
              />
            </div>

            <h4 className="proposer-title">7. Designation</h4>
            <div className="membership-form-group">
              <input 
                type="text" 
                name="designation" 
                value={formData.designation} 
                onChange={handleChange} 
                className="membership-input" 
              />
            </div>

            <h4 className="proposer-title">8. Office Address</h4>
            <div className="membership-form-group">
              <textarea 
                name="officeAddress" 
                rows="2" 
                value={formData.officeAddress} 
                onChange={handleChange} 
                className="membership-textarea" 
              ></textarea>
            </div>

            <h4 className="proposer-title">9. Residential Address</h4>
            <div className="membership-form-group">
              <textarea 
                name="residentialAddress" 
                rows="2" 
                value={formData.residentialAddress} 
                onChange={handleChange} 
                className="membership-textarea" 
                required
              ></textarea>
            </div>

            <h4 className="proposer-title">10. Area of Interest in Polycystic Kidney Disease</h4>
            <div className="membership-form-group">
              <div className="chip-container">
                {interestsList.map((interest) => {
                  const isSelected = formData.areaOfInterest.split(', ').includes(interest);

                  return (
                    <button
                      key={interest}
                      type="button"
                      className={`interest-chip ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleChipToggle(interest)}
                    >
                      {interest}
                    </button>
                  );
                })}
              </div>
            </div>

            <hr className="membership-divider" />
            
            <div className="payment-section-header">
                <div className="payment-header-top">
                    <div className="section-title-group">
                        <h3 className="payment-section-title">Payment Information</h3>
                        <p className="payment-subtitle">Review bank details and enter your transaction records below.</p>
                    </div>
                </div>
            </div>

            <div className="payment-details-wrapper">
              
              <div className="payment-left-col">
                <div className="payment-card">
                    <div className="payment-card-header">
                        <div className="payment-card-title-group">
                            <FaEdit className="payment-card-icon" />
                            <h4 className="payment-card-title">Transaction Details</h4>
                        </div>
                    </div>

                    <div className="payment-input-group">
                        <label className="membership-label-small">AMOUNT PAID (RS)</label>
                        <div className="input-with-prefix">
                            <span className="input-prefix">₹</span>
                            <input 
                                type="number" 
                                name="paidAmount" 
                                value={formData.paidAmount}
                                readOnly={true} 
                                onChange={handleChange} 
                                className="membership-input locked-input" 
                                required
                            />
                        </div>
                    </div>
                    
                    <div className="payment-input-group">
                        <label className="membership-label-small">PAYMENT MODE</label>
                        <select 
                            name="paymentMode" 
                            value={formData.paymentMode} 
                            onChange={handleChange} 
                            className="membership-select" 
                            required
                        >
                            <option value="">Select Payment Mode</option>
                            <option value="UPI">UPI</option>
                            <option value="NetBanking">Net Banking</option>
                            <option value="Cash">Cash</option>
                            <option value="Cheque">Cheque</option>
                        </select>
                    </div>

                    <div className="payment-input-group">
                        <label className="membership-label-small">TRANSACTION ID / UTR</label>
                        <input 
                            type="text" 
                            name="utrNumber" 
                            placeholder="Enter 12 digit UTR number"
                            value={formData.utrNumber} 
                            onChange={handleChange} 
                            className={formData.paymentMode === 'Cash' ? "membership-input locked-input" : "membership-input"} 
                            required={formData.paymentMode !== 'Cash'}
                            readOnly={formData.paymentMode === 'Cash'}
                        />
                    </div>

                    <div className="payment-input-group">
                        <label className="membership-label-small">PAYMENT DATE</label>
                        <input 
                            type="date" 
                            name="paymentDate" 
                            value={formData.paymentDate} 
                            onChange={handleChange} 
                            className="membership-input" 
                            max={todayDate}
                            required
                        />
                    </div>
                </div>
              </div>

              <div className="payment-right-col">
                <div className="payment-card bank-card">
                    
                    <div className="qr-section">
                        <div className="qr-image-wrapper">
                            <img 
                                src={qrScannerImg} 
                                alt="Scan to Pay QR Code" 
                                className="qr-image"
                            />
                        </div>
                    </div>
                    
                    <div className="payment-card-header bank-card-header-padded">
                        <div className="payment-card-title-group">
                            <FaUniversity className="payment-card-icon" />
                            <h4 className="payment-card-title">Bank Account Details</h4>
                        </div>
                    </div>
                    
                    <div className="bank-details-list">
                        <div className="bank-detail-row">
                            <span className="bank-detail-label">Account Holder</span>
                            <span className="bank-detail-value">INDIAN SOCIETY OF POLYCYSTIC DISEASE - ISPKD</span>
                        </div>
                        <div className="bank-detail-row">
                            <span className="bank-detail-label">Bank Name</span>
                            <span className="bank-detail-value">ICICI Bank</span>
                        </div>
                        <div className="bank-detail-row">
                            <span className="bank-detail-label">Account Number</span>
                            <span className="bank-detail-value">
                                584501000082 
                                <FaCopy className="copy-icon" onClick={() => handleCopy("584501000082")} title="Copy Account Number" />
                            </span>
                        </div>
                        <div className="bank-detail-row">
                            <span className="bank-detail-label">IFSC Code</span>
                            <span className="bank-detail-value">
                                ICIC0005845
                                <FaCopy className="copy-icon" onClick={() => handleCopy("ICIC0005845")} title="Copy IFSC Code" />
                            </span>
                        </div>
                    </div>

                </div>
              </div>

            </div>

            <hr className="membership-divider" />
            
            <div className="membership-form-group">
                <div className="declaration-group">
                    <label className="declaration-label">
                        <input 
                            type="checkbox" 
                            id="decl-apply"
                            name="applyMembership"
                            checked={declarations.applyMembership}
                            onChange={handleDeclarationChange}
                        /> 
                        <span>I hereby apply for membership of the <strong>Indian Society of Polycystic Kidney Disease (ISPKD)</strong>.</span>
                    </label>
                </div>
            </div>

            <div className="membership-form-group">
                <div className="declaration-group">
                    <label className="declaration-label">
                        <input 
                            type="checkbox" 
                            id="decl-rules"
                            name="agreeRules"
                            checked={declarations.agreeRules}
                            onChange={handleDeclarationChange}
                        /> 
                        <span>I agree to abide by the Constitution, Rules, and Regulations of Society.</span>
                    </label>
                </div>
            </div>

            <div className="submit-container">
              <button 
                type="submit" 
                className="btn btn-primary submit-btn"
                disabled={!isFormValid() || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>

          </form>

        </div>
      </div>
      <Footer />

      {showSuccessModal && (
            <div className="modal-overlay">
                <div className="modal-content">
                    <div className="modal-icon">
                        <FaCheckCircle />
                    </div>
                    <h3 className="modal-title">Success!</h3>
                    <p className="modal-message">
                        Application Submitted Successfully! <br/>
                        We will review your details and get back to you shortly.
                    </p>
                    <button className="modal-btn" onClick={closeModal}>
                        Close
                    </button>
                </div>
            </div>
      )}
    </>
  );
};

export default ApplyNewMembershipForm;